import { supabase } from '../supabaseClient';
import type {
  EmployeePayAssignment,
  EmploymentType,
  PayBand,
  PayBandRate,
} from './payroll/types';

/**
 * Pay band administration.
 *
 * Rates are effective dated: changing a band's pay closes the current rate and
 * opens a new one, so historic payroll runs keep reporting what was actually
 * paid at the time.
 */

interface CurrentProfile {
  id: string;
  company_id: string;
  role: string;
}

interface PayBandRow {
  id: string;
  company_id: string;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  is_default: boolean;
  sort_order: number;
}

interface PayBandRateRow {
  id: string;
  pay_band_id: string;
  effective_from: string;
  effective_to: string | null;
  base_hourly_rate: number | string;
  sunday_premium_multiplier: number | string | null;
  public_holiday_multiplier: number | string | null;
  unsocial_shift_premium: number | string | null;
  overtime_daily_multiplier: number | string | null;
  overtime_weekly_multiplier: number | string | null;
  double_time_multiplier: number | string | null;
  minimum_shift_pay_minutes: number | null;
  note: string | null;
}

interface AssignmentRow {
  id: string;
  company_id: string;
  guard_id: string;
  pay_band_id: string;
  effective_from: string;
  effective_to: string | null;
  hourly_rate_override: number | string | null;
  employment_type: string;
  contracted_weekly_hours: number | string | null;
  annual_leave_accrual_percent: number | string | null;
  payroll_reference: string | null;
  cost_centre: string | null;
  note: string | null;
  guard?: { full_name: string | null } | { full_name: string | null }[] | null;
  band?: { code: string; name: string } | { code: string; name: string }[] | null;
}

interface QueryPageResult {
  data: unknown[] | null;
  error: unknown;
}

const QUERY_PAGE_SIZE = 1000;

const PAY_BAND_SELECT = 'id, company_id, code, name, description, is_active, is_default, sort_order';

const PAY_BAND_RATE_SELECT = `
  id,
  pay_band_id,
  effective_from,
  effective_to,
  base_hourly_rate,
  sunday_premium_multiplier,
  public_holiday_multiplier,
  unsocial_shift_premium,
  overtime_daily_multiplier,
  overtime_weekly_multiplier,
  double_time_multiplier,
  minimum_shift_pay_minutes,
  note
`;

const ASSIGNMENT_SELECT = `
  id,
  company_id,
  guard_id,
  pay_band_id,
  effective_from,
  effective_to,
  hourly_rate_override,
  employment_type,
  contracted_weekly_hours,
  annual_leave_accrual_percent,
  payroll_reference,
  cost_centre,
  note,
  guard:profiles!employee_pay_assignments_guard_id_fkey(full_name),
  band:pay_bands!employee_pay_assignments_pay_band_id_fkey(code, name)
`;

const numberOrNull = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function unwrap<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

function todayKey(): string {
  const now = new Date();
  return [
    String(now.getFullYear()),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

/** Exhaust Supabase's bounded SELECT responses using a deterministic query. */
async function fetchAllPages<T>(
  fetchPage: (from: number, to: number) => PromiseLike<QueryPageResult>,
): Promise<T[]> {
  const rows: T[] = [];

  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    const { data, error } = await fetchPage(from, from + QUERY_PAGE_SIZE - 1);
    if (error) throw error;

    const page = (data ?? []) as T[];
    rows.push(...page);
    if (page.length < QUERY_PAGE_SIZE) return rows;
  }
}

async function getCurrentProfile(requireAdmin = false): Promise<CurrentProfile> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('profiles')
    .select('id, company_id, role')
    .eq('id', user.id)
    .single();

  if (error) throw error;
  if (!data?.company_id) throw new Error('This account is not attached to a company.');
  if (requireAdmin && data.role !== 'admin') {
    throw new Error('Only company admins can change pay bands.');
  }
  if (data.role !== 'admin' && data.role !== 'manager') {
    throw new Error('Only company admins and managers can view pay bands.');
  }

  return data as CurrentProfile;
}

function mapRate(row: PayBandRateRow): PayBandRate {
  return {
    id: row.id,
    payBandId: row.pay_band_id,
    effectiveFrom: row.effective_from,
    effectiveTo: row.effective_to,
    baseHourlyRate: numberOrNull(row.base_hourly_rate) ?? 0,
    sundayPremiumMultiplier: numberOrNull(row.sunday_premium_multiplier),
    publicHolidayMultiplier: numberOrNull(row.public_holiday_multiplier),
    unsocialShiftPremium: numberOrNull(row.unsocial_shift_premium),
    overtimeDailyMultiplier: numberOrNull(row.overtime_daily_multiplier),
    overtimeWeeklyMultiplier: numberOrNull(row.overtime_weekly_multiplier),
    doubleTimeMultiplier: numberOrNull(row.double_time_multiplier),
    minimumShiftPayMinutes: row.minimum_shift_pay_minutes,
    note: row.note,
  };
}

/** The rate covering today, else the most recent one that started. */
function pickCurrentRate(rates: PayBandRate[]): PayBandRate | null {
  const today = todayKey();
  const covering = rates.find((rate) => (
    rate.effectiveFrom <= today && (!rate.effectiveTo || today <= rate.effectiveTo)
  ));
  if (covering) return covering;

  const started = rates.filter((rate) => rate.effectiveFrom <= today);
  if (started.length) return started[started.length - 1];
  return rates[0] ?? null;
}

function mapAssignment(row: AssignmentRow): EmployeePayAssignment {
  const guard = unwrap(row.guard);
  const band = unwrap(row.band);

  return {
    id: row.id,
    companyId: row.company_id,
    guardId: row.guard_id,
    guardName: guard?.full_name?.trim() || 'Unknown guard',
    payBandId: row.pay_band_id,
    payBandCode: band?.code ?? '',
    payBandName: band?.name ?? '',
    effectiveFrom: row.effective_from,
    effectiveTo: row.effective_to,
    hourlyRateOverride: numberOrNull(row.hourly_rate_override),
    effectiveHourlyRate: null,
    employmentType: (row.employment_type || 'full_time') as EmploymentType,
    contractedWeeklyHours: numberOrNull(row.contracted_weekly_hours),
    annualLeaveAccrualPercent: numberOrNull(row.annual_leave_accrual_percent),
    payrollReference: row.payroll_reference,
    costCentre: row.cost_centre,
    note: row.note,
  };
}

export interface PayBandInput {
  code: string;
  name: string;
  description?: string | null;
  isActive?: boolean;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface PayBandRateInput {
  payBandId: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  baseHourlyRate: number;
  sundayPremiumMultiplier?: number | null;
  publicHolidayMultiplier?: number | null;
  unsocialShiftPremium?: number | null;
  overtimeDailyMultiplier?: number | null;
  overtimeWeeklyMultiplier?: number | null;
  doubleTimeMultiplier?: number | null;
  minimumShiftPayMinutes?: number | null;
  note?: string | null;
}

export interface AssignmentInput {
  guardId: string;
  payBandId: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  hourlyRateOverride?: number | null;
  employmentType?: EmploymentType;
  contractedWeeklyHours?: number | null;
  annualLeaveAccrualPercent?: number | null;
  payrollReference?: string | null;
  costCentre?: string | null;
  note?: string | null;
}

export const payBandService = {
  async listPayBands(): Promise<PayBand[]> {
    const profile = await getCurrentProfile();

    const [bandRows, rateRows, assignmentRows] = await Promise.all([
      fetchAllPages<PayBandRow>((from, to) => supabase
          .from('pay_bands')
          .select(PAY_BAND_SELECT)
          .eq('company_id', profile.company_id)
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to)),
      fetchAllPages<PayBandRateRow>((from, to) => supabase
          .from('pay_band_rates')
          .select(PAY_BAND_RATE_SELECT)
          .eq('company_id', profile.company_id)
          .order('effective_from', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to)),
      fetchAllPages<{
        id: string;
        pay_band_id: string;
        effective_from: string;
        effective_to: string | null;
      }>((from, to) => supabase
          .from('employee_pay_assignments')
          .select('id, pay_band_id, effective_from, effective_to')
          .eq('company_id', profile.company_id)
          .order('id', { ascending: true })
          .range(from, to)),
    ]);

    const ratesByBand = new Map<string, PayBandRate[]>();
    rateRows.forEach((row) => {
      const rate = mapRate(row);
      if (!ratesByBand.has(rate.payBandId)) ratesByBand.set(rate.payBandId, []);
      ratesByBand.get(rate.payBandId)!.push(rate);
    });

    const today = todayKey();
    const assignedByBand = new Map<string, number>();
    assignmentRows.forEach((row) => {
      // Count only the assignment that covers today. Future scheduled moves
      // must not make one employee appear assigned to two bands at once.
      if (row.effective_from > today || (row.effective_to && row.effective_to < today)) return;
      assignedByBand.set(row.pay_band_id, (assignedByBand.get(row.pay_band_id) ?? 0) + 1);
    });

    return bandRows.map((row) => {
      const rates = ratesByBand.get(row.id) ?? [];
      return {
        id: row.id,
        companyId: row.company_id,
        code: row.code,
        name: row.name,
        description: row.description,
        isActive: row.is_active,
        isDefault: row.is_default,
        sortOrder: row.sort_order,
        rates,
        currentRate: pickCurrentRate(rates),
        assignedCount: assignedByBand.get(row.id) ?? 0,
      };
    });
  },

  async createPayBand(input: PayBandInput, initialRate: number): Promise<PayBand> {
    await getCurrentProfile(true);

    const { data, error } = await supabase
      .rpc('create_pay_band_with_initial_rate', {
        p_code: input.code.trim().toUpperCase(),
        p_name: input.name.trim(),
        p_description: input.description ?? null,
        p_is_active: input.isActive ?? true,
        p_is_default: input.isDefault ?? false,
        p_sort_order: input.sortOrder ?? 0,
        p_initial_rate: initialRate,
        p_effective_from: todayKey(),
      })
      .single();

    if (error) throw error;

    const band = data as PayBandRow;
    return {
      id: band.id,
      companyId: band.company_id,
      code: band.code,
      name: band.name,
      description: band.description,
      isActive: band.is_active,
      isDefault: band.is_default,
      sortOrder: band.sort_order,
      rates: [],
      currentRate: null,
      assignedCount: 0,
    };
  },

  async updatePayBand(payBandId: string, input: Partial<PayBandInput>): Promise<void> {
    const profile = await getCurrentProfile(true);

    const payload: Record<string, unknown> = { updated_by: profile.id };
    if (input.code !== undefined) payload.code = input.code.trim().toUpperCase();
    if (input.name !== undefined) payload.name = input.name.trim();
    if (input.description !== undefined) payload.description = input.description;
    if (input.isActive !== undefined) payload.is_active = input.isActive;
    if (input.isDefault !== undefined) payload.is_default = input.isDefault;
    if (input.sortOrder !== undefined) payload.sort_order = input.sortOrder;

    const { error } = await supabase
      .from('pay_bands')
      .update(payload)
      .eq('id', payBandId)
      .eq('company_id', profile.company_id);

    if (error) throw error;
  },

  async deletePayBand(payBandId: string): Promise<void> {
    const profile = await getCurrentProfile(true);

    const { error } = await supabase
      .from('pay_bands')
      .delete()
      .eq('id', payBandId)
      .eq('company_id', profile.company_id);

    if (error) {
      // The assignment FK is ON DELETE RESTRICT, so this is the common failure.
      if ((error as { code?: string }).code === '23503') {
        throw new Error('This pay band still has employees assigned. Move them to another band first.');
      }
      throw error;
    }
  },

  /**
   * Open a new rate from `effectiveFrom`, closing the previous open-ended rate
   * the day before so the two windows never overlap.
   */
  async addRate(input: PayBandRateInput): Promise<PayBandRate> {
    await getCurrentProfile(true);

    const { data, error } = await supabase
      .rpc('add_pay_band_rate', {
        p_pay_band_id: input.payBandId,
        p_effective_from: input.effectiveFrom,
        p_effective_to: input.effectiveTo ?? null,
        p_base_hourly_rate: input.baseHourlyRate,
        p_sunday_premium_multiplier: input.sundayPremiumMultiplier ?? null,
        p_public_holiday_multiplier: input.publicHolidayMultiplier ?? null,
        p_unsocial_shift_premium: input.unsocialShiftPremium ?? null,
        p_overtime_daily_multiplier: input.overtimeDailyMultiplier ?? null,
        p_overtime_weekly_multiplier: input.overtimeWeeklyMultiplier ?? null,
        p_double_time_multiplier: input.doubleTimeMultiplier ?? null,
        p_minimum_shift_pay_minutes: input.minimumShiftPayMinutes ?? null,
        p_note: input.note ?? null,
      })
      .single();

    if (error) {
      if ((error as { code?: string }).code === '23505') {
        throw new Error('That rate period overlaps an existing rate for this band.');
      }
      throw error;
    }

    return mapRate(data as PayBandRateRow);
  },

  async deleteRate(_rateId: string): Promise<void> {
    await getCurrentProfile(true);
    throw new Error('Pay-band rate history cannot be deleted. Schedule a replacement rate from its effective date instead.');
  },

  async listAssignments(): Promise<EmployeePayAssignment[]> {
    const profile = await getCurrentProfile();

    const data = await fetchAllPages<AssignmentRow>((from, to) => supabase
      .from('employee_pay_assignments')
      .select(ASSIGNMENT_SELECT)
      .eq('company_id', profile.company_id)
      .order('effective_from', { ascending: false })
      .order('id', { ascending: false })
      .range(from, to));

    return data.map(mapAssignment);
  },

  /**
   * Move an employee onto a band from a date. Any open assignment starting
   * earlier is closed the day before, which is what "give them a raise from
   * Monday" means in practice.
   */
  async assignEmployee(input: AssignmentInput): Promise<EmployeePayAssignment> {
    await getCurrentProfile(true);

    const { data, error } = await supabase
      .rpc('assign_employee_pay_band', {
        p_guard_id: input.guardId,
        p_pay_band_id: input.payBandId,
        p_effective_from: input.effectiveFrom,
        p_effective_to: input.effectiveTo ?? null,
        p_hourly_rate_override: input.hourlyRateOverride ?? null,
        p_employment_type: input.employmentType ?? 'full_time',
        p_contracted_weekly_hours: input.contractedWeeklyHours ?? null,
        p_annual_leave_accrual_percent: input.annualLeaveAccrualPercent ?? null,
        p_payroll_reference: input.payrollReference ?? null,
        p_cost_centre: input.costCentre ?? null,
        p_note: input.note ?? null,
      })
      .single();

    if (error) {
      if ((error as { code?: string }).code === '23505') {
        throw new Error('That assignment period overlaps an existing one for this employee.');
      }
      throw error;
    }

    return mapAssignment(data as AssignmentRow);
  },

  async updateAssignment(
    _assignmentId: string,
    _input: Partial<AssignmentInput>,
  ): Promise<void> {
    await getCurrentProfile(true);
    throw new Error('Employee pay assignment history cannot be overwritten. Schedule a new assignment from its effective date instead.');
  },

  async deleteAssignment(_assignmentId: string): Promise<void> {
    await getCurrentProfile(true);
    throw new Error('Employee pay assignment history cannot be deleted. Schedule a replacement assignment instead.');
  },

  /** Guards with no assignment covering today, so the UI can prompt to band them. */
  async listUnbandedGuards(): Promise<{ id: string; fullName: string }[]> {
    const profile = await getCurrentProfile();
    const today = todayKey();

    const [guards, assignments] = await Promise.all([
      fetchAllPages<{ id: string; full_name: string | null }>((from, to) => supabase
        .from('profiles')
        .select('id, full_name')
        .eq('company_id', profile.company_id)
        .eq('is_guard', true)
        .eq('status', 'active')
        .order('full_name', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to)),
      fetchAllPages<{ guard_id: string; effective_from: string; effective_to: string | null }>((from, to) => supabase
        .from('employee_pay_assignments')
        .select('guard_id, effective_from, effective_to')
        .eq('company_id', profile.company_id)
        .order('guard_id', { ascending: true })
        .order('effective_from', { ascending: true })
        .range(from, to)),
    ]);

    const covered = new Set(
      assignments
        .filter((row) => row.effective_from <= today && (!row.effective_to || today <= row.effective_to))
        .map((row) => row.guard_id),
    );

    return guards
      .filter((guard) => !covered.has(guard.id))
      .map((guard) => ({ id: guard.id, fullName: guard.full_name?.trim() || 'Unknown guard' }));
  },
};
